import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { canLogInToWeb, isDistributorAdmin } from '../utils/roles';
import {
  SESSION_KEY,
  SESSION_EVENT,
  captureSession,
  assertCurrentSession,
  isSessionCurrent,
  advanceSession,
  clearCredentials,
} from '../utils/authSession';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionVersion, setSessionVersion] = useState(null);

  useEffect(() => {
    const restore = () => {
      let restored = null;
      try {
        if (localStorage.getItem('accessToken')) {
          const stored = JSON.parse(localStorage.getItem('user'));
          if (stored && canLogInToWeb(stored.role)) restored = stored;
        }
      } catch {
        // Leave language/preferences intact. Cached users never establish legal access: the
        // protected boundary verifies the authenticated profile and receipt with the server.
        ['accessToken', 'refreshToken', 'user'].forEach((key) => localStorage.removeItem(key));
      }
      setUser(restored);
      setIsAuthenticated(Boolean(restored));
      setSessionVersion(localStorage.getItem(SESSION_KEY));
      setIsLoading(false);
    };
    const onStorage = (event) => {
      if (!event.key || [SESSION_KEY, 'accessToken', 'refreshToken', 'user'].includes(event.key))
        restore();
    };
    restore();
    window.addEventListener('storage', onStorage);
    window.addEventListener(SESSION_EVENT, restore);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SESSION_EVENT, restore);
    };
  }, []);

  const login = useCallback(async (email, password) => {
    clearCredentials();
    const attempt = captureSession();
    const response = await authService.login(email, password);
    assertCurrentSession(attempt);
    const { accessToken, refreshToken, user: userData } = response.data.data;

    // Role gate: direct web accounts only. The throw happens BEFORE any token is
    // persisted - a rejected role must leave no session behind.
    if (
      !canLogInToWeb(userData.role) ||
      userData.role === 'master_distributor' ||
      isDistributorAdmin(userData)
    ) {
      throw new Error('Access denied. This portal is for MedEffects staff and clinic accounts.');
    }

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    advanceSession();
    setUser(userData);
    setIsAuthenticated(true);
    setSessionVersion(localStorage.getItem(SESSION_KEY));
    return userData;
  }, []);

  const distributorLogin = useCallback(async (username, password) => {
    clearCredentials();
    const attempt = captureSession();
    const response = await authService.distributorLogin(username, password);
    assertCurrentSession(attempt);
    const { accessToken, refreshToken, user: userData } = response.data.data;
    if (userData?.role !== 'master_distributor' && !isDistributorAdmin(userData)) {
      throw new Error('Access denied. This portal is for distributor accounts.');
    }
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    advanceSession();
    setUser(userData);
    setIsAuthenticated(true);
    setSessionVersion(localStorage.getItem(SESSION_KEY));
    return userData;
  }, []);

  /**
   * Merge a change into the signed-in user, in state AND in localStorage, so a profile edit on
   * Settings shows up in the sidebar name and initials at once - without a sign-out - and survives
   * a reload. No-op when nobody is signed in.
   */
  const updateUser = useCallback((patch, expectedSession) => {
    setUser((current) => {
      if (!current || (expectedSession && !isSessionCurrent(expectedSession))) return current;
      const next = { ...current, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    clearCredentials();
    setUser(null);
    setIsAuthenticated(false);
    setSessionVersion(localStorage.getItem(SESSION_KEY));
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    sessionVersion,
    login,
    distributorLogin,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
